import { useProductsLookup } from '@client/store/product';
import { useRecipeLookup } from '@client/store/recipe/recipe.hook';
import { useProjectState } from '@client/store/project';
import { getSelectedVersion, useSelectedVersion } from '@client/store/ui';
import {
  useApplyProjectChanges,
  type ApplyProjectChangesOptions,
} from '@client/store/project/use-apply-project-changes.hook';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import {
  candidateUnitFrom,
  hashImportMapping,
} from '@shared/helpers/materialize-candidate.helpers';
import { draftToOriginMapping } from '@shared/helpers/import-row-draft.helpers';
import type { Baseline, IElement } from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';
import { useElementImports } from './use-element-imports.hook';
import {
  buildFailedImportMessage,
  useAutoImportCandidates,
} from './use-auto-import.hook';
import {
  getElementImportsData,
  type MappingSaveScope,
} from './element-imports.store';
import {
  buildSameCodeDiscardChanges,
  buildSameCodeRecreateChanges,
  getSameCodeSiblings,
} from './same-code-rows.helpers';
import type { SaveMappingOptions } from './SaveMappingDialog';
import { collectImportedElements } from './candidate-status.helpers';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import {
  groupImportedElementAdds,
  IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE,
} from '@shared/helpers/import-replace.helpers';
import { selectedProposalIdForVersion } from './import-replace-target.helpers';
import {
  mappingFromElementOrigin,
  resolveSaveMappingAction,
  saveMappingBatchAction,
  saveMappingSuccessMessage,
  selectSaveMappingTargetRows,
  type SaveMappingSnapshot,
} from './save-mapping-action.helpers';
import type {
  CandidateRow,
  ElementOriginMapping,
  ImportRowDraft,
} from '@shared/models/element-import.model';
import { useCandidateConversionEditor } from './use-candidate-conversion-editor.hook';
import {
  getListSelection,
  getVisibleListRowIds,
  useListSelection,
} from './list-selection.store';

/**
 * Overlay that applies one draft uniformly to every sibling, keyed per row so
 * each materializes through its own `unitFrom`. Built in-memory because the
 * drafts are written in the same batch (not yet on the project) — reading them
 * back from the version would miss them.
 */
const buildSiblingDraftOverlay = (
  siblings: readonly CandidateRow[],
  draft: ImportRowDraft,
): Record<string, ElementOriginMapping | undefined> => {
  const out: Record<string, ElementOriginMapping | undefined> = {};
  for (const sibling of siblings) {
    const unitFrom = candidateUnitFrom(sibling);
    if (!unitFrom) continue;
    const mapping = draftToOriginMapping(draft, unitFrom);
    if (mapping) out[sibling.id] = mapping;
  }
  return out;
};

type UseCandidateSaveMappingArgs = {
  row: CandidateRow;
  importedElement?: IElement;
};

type ApplyChangesFn = ReturnType<typeof useApplyProjectChanges>;

/**
 * The mapping row itself lives outside the project (resource-code table), so
 * undoing the frame this batch produces reverts only the row rebuilds — the
 * undo snackbar derives its warning from this label.
 */
const SAVED_MAPPING_EFFECT = 'Saved mapping';

/**
 * Apply {@link ProjectChange}s and resolve as soon as the optimistic local write lands,
 * letting enrichment + project PUT run in the background (DEV-3440). The optimistic update
 * already reflects the saved mapping, so the user no longer waits on enrich/persist.
 * Enrichment/persist failures still surface via {@link useUpdateProject}'s own revert +
 * snackbar handling. Falls back to the settled result when no optimistic write runs
 * (e.g. empty batch or pre-apply validation error).
 */
const applyChangesOptimistically = (
  applyChanges: ApplyChangesFn,
  changes: ProjectChange[],
  options: ApplyProjectChangesOptions = {},
): Promise<void> => {
  if (changes.length === 0) return Promise.resolve();
  // resolve() / reject() settle the Promise once; later no-op. So whichever of
  // the optimistic callback or settled applyChanges fires first wins, and a
  // pre-optimistic rejection still propagates instead of hanging.
  return new Promise<void>((resolve, reject) => {
    void applyChanges(changes, {
      ...options,
      onOptimisticLocalUpdate: resolve,
    }).then(() => resolve(), reject);
  });
};

const collectSaveMappingTargets = (
  sourceRow: CandidateRow,
  projectId: number,
  selectedIds: ReadonlySet<string>,
  version: Baseline | undefined,
  proposalId: ProposalId | undefined,
): SaveMappingSnapshot[] => {
  const data = getElementImportsData(projectId);
  if (!data) return [];
  return selectSaveMappingTargetRows(
    sourceRow,
    Object.values(data.rowsByImport).flat(),
    selectedIds,
    getVisibleListRowIds(),
    {
      version,
      imported: collectImportedElements(version, proposalId),
      mappingsByCode: data.mappingsByCode,
    },
  );
};

const siblingDraftFromSnapshot = (
  snapshot: SaveMappingSnapshot,
): ImportRowDraft | null =>
  snapshot.referenceId
    ? {
        referenceId: snapshot.referenceId,
        referenceType: snapshot.referenceType,
        unitTo: snapshot.entry.unitTo,
        quantityFactor: snapshot.entry.quantityFactor,
        selectedQuantity: snapshot.entry.quantityName,
      }
    : null;

const originChangeFromSnapshot = (
  snapshot: SaveMappingSnapshot,
  target: IElement,
  scope: MappingSaveScope,
): ProjectChange | undefined => {
  if (!snapshot.referenceId || !snapshot.unitFrom) return undefined;
  if (target.origin?.kind !== 'csvImport') return undefined;
  const mapping: ElementOriginMapping = {
    referenceId: snapshot.referenceId,
    referenceType: snapshot.referenceType,
    mappings: { [snapshot.unitFrom]: snapshot.entry },
  };
  return {
    type: ProjectChangeType.ElementOrigin,
    elementId: target.id,
    origin: {
      kind: 'csvImport',
      importId: snapshot.row.importId,
      candidateRowId: snapshot.row.id,
      mappingHash: hashImportMapping(mapping),
      trigger: target.origin.trigger,
      source: 'stored',
      scope,
    },
  };
};

export const useCandidateSaveMapping = ({
  row,
  importedElement,
}: UseCandidateSaveMappingArgs) => {
  const { project } = useProjectState('project');
  const imports = useElementImports(project?.id ?? -1);
  const productsById = useProductsLookup();
  const recipesById = useRecipeLookup();
  const version = useSelectedVersion();
  const proposalId = useSelectedProposalId();
  const applyChanges = useApplyProjectChanges();
  const { buildImportChanges } = useAutoImportCandidates();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedIds } = useListSelection();

  const {
    unitFrom,
    seededFactor,
    mappedProduct,
    mappedRecipe,
    referenceId,
    referenceType,
    isDiscarded,
    buildRateEntry,
  } = useCandidateConversionEditor({ row, importedElement });

  const canSaveMapping =
    isDiscarded ||
    !!mappedProduct ||
    !!mappedRecipe ||
    seededFactor !== undefined;

  const importsData = getElementImportsData(project?.id ?? -1);
  const stored = row.resourceCode
    ? importsData?.mappingsByCode[row.resourceCode]
    : undefined;
  const originMapping = mappingFromElementOrigin(
    importedElement?.origin,
    stored,
  );
  const currentMapping: ElementOriginMapping | undefined =
    originMapping ??
    (referenceId && unitFrom
      ? {
          referenceId,
          referenceType,
          mappings: { [unitFrom]: buildRateEntry(seededFactor) },
        }
      : undefined);
  const saveMappingAction = resolveSaveMappingAction({
    isDiscarded,
    unitFrom,
    current: currentMapping,
    stored,
  });

  // Resource code keys the DB row; non-skip saves also need a file unit.
  // Unchanged stored mappings are omitted — there is nothing to persist.
  const canOpenSaveDialog =
    Boolean(row.resourceCode) &&
    canSaveMapping &&
    (isDiscarded || !!unitFrom) &&
    saveMappingAction !== undefined;

  const saveMappingTargets =
    project && canOpenSaveDialog
      ? collectSaveMappingTargets(
          row,
          project.id,
          selectedIds,
          version,
          proposalId,
        )
      : [];
  const saveMappingTargetCount =
    saveMappingTargets.length || (canOpenSaveDialog ? 1 : 0);
  const saveMappingBatchUiAction =
    saveMappingTargets.length > 0
      ? saveMappingBatchAction(
          saveMappingTargets.map((target) => target.action),
        )
      : saveMappingAction;

  const siblingCount =
    (isDiscarded || referenceId) && importsData
      ? getSameCodeSiblings(row, importsData.rowsByImport).length
      : 0;
  const matchingRowCount = saveMappingTargetCount > 1 ? 0 : siblingCount;

  const handleSaveMapping = async ({
    scope,
    alsoMapMatching,
  }: SaveMappingOptions) => {
    if (imports.status !== 'ready' || !project) {
      throw new Error('Imports not ready');
    }

    const snapshots = collectSaveMappingTargets(
      row,
      project.id,
      getListSelection().selectedIds,
      getSelectedVersion(),
      proposalId,
    );
    if (snapshots.length === 0) {
      throw new Error('Mapping incomplete');
    }

    // One transaction for the whole selection: the element/origin changes
    // below assume every code landed in the table.
    await imports.upsertMappings(
      snapshots.map((snapshot) => ({
        resourceCode: snapshot.resourceCode,
        referenceId: snapshot.referenceId,
        referenceType: snapshot.referenceType,
        unitFrom: snapshot.unitFrom,
        entry: snapshot.entry,
        discard: snapshot.isDiscarded,
      })),
      scope,
    );

    const freshData = getElementImportsData(project.id);
    const currentVersion = getSelectedVersion();
    const imported = collectImportedElements(currentVersion, proposalId);
    const spreadSiblings = alsoMapMatching && snapshots.length === 1;
    const [single] = snapshots;
    const changes: ProjectChange[] = [];
    const rowsToImport: CandidateRow[] = [];

    for (const snapshot of snapshots) {
      if (snapshot.isDiscarded) continue;
      const target = imported.get(snapshot.row.id);
      const originChange = target
        ? originChangeFromSnapshot(snapshot, target, scope)
        : undefined;
      if (originChange) {
        changes.push(originChange);
        continue;
      }
      if (!target) rowsToImport.push(snapshot.row);
    }

    if (spreadSiblings && single && currentVersion && freshData) {
      const siblings = getSameCodeSiblings(single.row, freshData.rowsByImport);
      const siblingDraft = siblingDraftFromSnapshot(single);
      if (single.isDiscarded) {
        changes.push(
          ...buildSameCodeDiscardChanges(siblings, {
            version: currentVersion,
            mappingsByCode: freshData.mappingsByCode,
            productsById,
            recipesById,
            importsById: freshData.importsById,
            draftMappingsByRowId: siblingDraft
              ? buildSiblingDraftOverlay(siblings, siblingDraft)
              : {},
            siblingDraft,
          }),
        );
      } else if (single.referenceId && single.unitFrom) {
        const refill = siblingDraft;
        if (refill) {
          const recreated = buildSameCodeRecreateChanges(siblings, {
            version: currentVersion,
            mappingsByCode: freshData.mappingsByCode,
            productsById,
            recipesById,
            importsById: freshData.importsById,
            draftMappingsByRowId: buildSiblingDraftOverlay(siblings, refill),
            siblingDraft: refill,
          });
          const grouped = groupImportedElementAdds(
            currentVersion,
            selectedProposalIdForVersion(currentVersion),
            recreated,
          );
          if (grouped.needsProposal) {
            enqueueSnackbar(IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE, {
              variant: 'warning',
            });
          } else {
            changes.push(...grouped.changes);
          }
        }
      }
    }

    if (rowsToImport.length > 0) {
      const {
        changes: importChanges,
        failedRows,
        needsProposal,
      } = buildImportChanges(rowsToImport, 'user');
      if (needsProposal) {
        enqueueSnackbar(IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE, {
          variant: 'warning',
        });
      } else {
        changes.push(...importChanges);
      }
      const failedMessage = buildFailedImportMessage(failedRows);
      if (failedMessage) {
        enqueueSnackbar(failedMessage, { variant: 'error' });
      }
    }

    await applyChangesOptimistically(applyChanges, changes, {
      effectsOutsideUndo: [SAVED_MAPPING_EFFECT],
    });
    enqueueSnackbar(
      saveMappingSuccessMessage(
        saveMappingBatchAction(snapshots.map((snapshot) => snapshot.action)),
        snapshots.length,
      ),
      { variant: 'success' },
    );
  };

  return {
    canSaveMapping,
    canOpenSaveDialog,
    saveMappingAction: saveMappingBatchUiAction,
    saveMappingTargetCount,
    matchingRowCount,
    handleSaveMapping,
    mappedProduct,
  };
};
